import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { fetchProfile, updateProfile } from "./nativeProfile";
import { uploadImage, ALLOWED_IMAGE_TYPES } from "./uploadImage";
import LocationPicker from "./LocationPicker";
import { InlineLoader, Spinner } from "./PageLoader";
import PhoneField from "./PhoneField";
import { DEFAULT_ABOUT } from "./constants";
import AffiliateEarnSection from "./AffiliateEarnSection";

// A "blank" banner is the placeholder asset — shown grayscale like the community
// hero so default headers read as monochrome rather than a flat colour block.
/**
 * Name bounds match the admin app's member rules (MEMBER_LIMITS in
 * membership-admin's CreateMemberModal, and the server's min:2|max:150), so a
 * name a learner can set here is one the admin side will also accept.
 * ABOUT_MAX mirrors the textarea's own maxLength below.
 */
const NAME_LIMITS = { min: 2, max: 150 };
const ABOUT_MAX = 1000;

const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);

/**
 * Member profile modal — view stats + edit name/phone.
 *
 * `embedded` renders the same header and form as a page card (Spotlight's
 * My Profile page) instead of a popup: no overlay, no close button, no Cancel.
 */
function ProfileModal({ token, member, groupToken, primary = "#0274F7", banner, onClose, onUpdated, onPointsChange, embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [about, setAbout] = useState("");
  // Keyed by field, so each message renders under its own input rather than as
  // the browser's native validation bubble.
  const [errors, setErrors] = useState({});
  const [loc, setLoc] = useState({ location: "", country: "", state: "", city: "", latitude: null, longitude: null });
  const [privacy, setPrivacy] = useState("city");
  const [img, setImg] = useState(member?.img || "");
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = { data: await fetchProfile({
          funnelId: member?.funnel_id,
          memberId: member?.community_member_id,
          jwt: member?.member_token,
        }) };
        if (!active) return;
        if (res.data?.error === 0) {
          const m = res.data.member;
          setData(m);
          setName(m.name || "");
          setPhone(m.phone || "");
          setImg(m.img || "");
          setAbout(m.about || DEFAULT_ABOUT);
          setPrivacy(m.location_privacy || "city");
          setLoc({
            location: m.location || "",
            country: m.country || "",
            state: m.state || "",
            city: m.city || "",
            latitude: m.latitude ?? null,
            longitude: m.longitude ?? null,
          });
        } else {
          toast.error(res.data?.msg || "Could not load profile.");
        }
      } catch (err) {
        if (active) toast.error("Could not load profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, member]);

  // Background scroll is locked globally by <ScrollLockManager/>, which detects
  // this modal's `fixed inset-0` overlay while it's mounted. Locking body.style
  // here as well makes the two capture each other's value and can leave the page
  // frozen after close — so we intentionally do NOT touch body.style here.

  const save = async (e) => {
    e.preventDefault();

    // Collect every problem in one pass so nothing is discovered one submit at
    // a time. `name` is the only required field — phone, about and location are
    // all marked optional in the form.
    const next = {};
    const trimmedName = name.trim();
    if (!trimmedName) {
      next.name = "Name is required.";
    } else if (trimmedName.length < NAME_LIMITS.min) {
      next.name = `Name must be at least ${NAME_LIMITS.min} characters.`;
    } else if (trimmedName.length > NAME_LIMITS.max) {
      next.name = `Name can't exceed ${NAME_LIMITS.max} characters.`;
    }
    if (about && about.length > ABOUT_MAX) {
      next.about = `About can't exceed ${ABOUT_MAX} characters.`;
    }

    // Location is optional, but it has to be a REAL place. Text typed without
    // choosing a suggestion arrives with no coordinates — that can't be plotted
    // on the member map, so reject it rather than storing a label that points
    // nowhere. Clearing the field is always allowed.
    if ((loc.location || "").trim() && (loc.latitude == null || loc.longitude == null)) {
      next.location = "Choose a location from the suggestions, or clear the field.";
    }

    const problems = Object.values(next);
    if (problems.length) {
      setErrors(next);
      toast.error(problems.length === 1 ? problems[0] : "Please fix the highlighted fields.");

      return;
    }
    setErrors({});
    setSaving(true);
    try {
      // Native api/profileupdate persists name + profile_img + location/contact.
      const res = { data: await updateProfile({
        funnelId: member?.funnel_id,
        memberId: member?.community_member_id,
        jwt: member?.member_token,
        name,
        img,
        phone,
        about,
        location: loc.location,
        country: loc.country,
        state: loc.state,
        city: loc.city,
        latitude: loc.latitude,
        longitude: loc.longitude,
        location_privacy: privacy,
      }) };
      if (res.data?.error === 0) {
        // A first profile photo earns the membership's gamification points. Say
        // so and refresh the total, otherwise the balance changes silently and
        // the reward looks like it never landed.
        const earned = Number(res.data.points_awarded || 0);
        toast.success(earned > 0 ? `Profile updated. +${earned} points!` : "Profile updated.");
        setData((d) => ({ ...d, name, phone, about, ...loc, location_privacy: privacy }));
        onUpdated?.({ ...member, name });
        if (earned > 0) onPointsChange?.();
      } else {
        toast.error(res.data?.msg || "Could not update.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    // The image service takes JPEG and PNG — the same pair the legacy learner
    // app enforced. Checking here turns an opaque server-side rejection into a
    // message that says which formats work.
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Please choose a JPG or PNG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      // 1) Upload to the FlexiFunnels image service — the same endpoint and
      //    payload the legacy learner app used, so avatars live alongside every
      //    other FlexiFunnels image instead of on this app's own disk (the old
      //    path here reused membership-chat-upload, which writes to
      //    storage/app/public/chat and serves off our own domain).
      let rawUrl;
      try {
        rawUrl = await uploadImage(file, {
          userId: member?.community_member_id,
          jwt: member?.member_token,
        });
      } catch (e) {
        toast.error(e.message || "Could not upload photo.");
        return;
      }
      // 2) Persist it on the profile (native api/profileupdate saves profile_img).
      //    Send the current field values so nothing else is wiped.
      const res = await updateProfile({
        funnelId: member?.funnel_id,
        memberId: member?.community_member_id,
        jwt: member?.member_token,
        name,
        img: rawUrl,
        phone,
        about,
        location: loc.location,
        country: loc.country,
        state: loc.state,
        city: loc.city,
        latitude: loc.latitude,
        longitude: loc.longitude,
        location_privacy: privacy,
      });
      if (res.error === 0) {
        // cache-bust so the new image shows immediately
        const url = `${rawUrl}?t=${Date.now()}`;
        setImg(url);
        const earned = Number(res.points_awarded || 0);
        toast.success(earned > 0 ? `Photo updated. +${earned} points!` : "Photo updated.");
        onUpdated?.({ ...member, img: url });
        onPointsChange?.();
      } else {
        toast.error(res.msg || "Could not upload photo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not upload photo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  /**
   * Remove the profile photo — persisted straight away, like the upload it
   * mirrors, so the member never has to press Save to make it stick.
   * Points already earned for the first photo are NOT taken back (that's what
   * stops remove/re-upload from farming them).
   */
  const removeAvatar = async () => {
    if (!img || removing) return;
    setRemoving(true);
    try {
      const res = await updateProfile({
        funnelId: member?.funnel_id,
        memberId: member?.community_member_id,
        jwt: member?.member_token,
        name,
        img: "",
        phone,
        about,
        location: loc.location,
        country: loc.country,
        state: loc.state,
        city: loc.city,
        latitude: loc.latitude,
        longitude: loc.longitude,
        location_privacy: privacy,
      });
      if (res.error === 0) {
        setImg("");
        toast.success("Photo removed.");
        onUpdated?.({ ...member, img: "" });
      } else {
        toast.error(res.msg || "Could not remove photo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not remove photo.");
    } finally {
      setRemoving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const initial = (name || member?.name || "?").charAt(0).toUpperCase();

  return (
    <div
      className={embedded ? "w-full" : "fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center bg-ink/60 backdrop-blur-sm p-0 sm:p-4"}
      /* No backdrop-click close: this is a long form and a stray click on the
         dim area used to discard everything typed. Close via the X or Cancel. */
    >
      <div
        className={embedded
          ? "bg-surface w-full rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden"
          : "bg-surface w-full h-full sm:h-auto sm:max-w-2xl rounded-none sm:rounded-3xl shadow-2xl sm:ring-1 sm:ring-black/5 max-h-none sm:max-h-[94vh] flex flex-col overflow-hidden"}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Branded header: community banner in the background, tinted with the
            selected theme colour so the text on top stays readable. */}
        <div className="relative shrink-0 px-6 pt-8 pb-7 text-center overflow-hidden bg-ink">
          {banner && (
            <div
              aria-hidden="true"
              className={`absolute inset-0 z-0 bg-center bg-cover${
                isEmptyBanner(banner) ? " grayscale" : ""
              }`}
              style={{ backgroundImage: `url("${banner}")` }}
            />
          )}
          {/* Light theme-colour overlay over the (grayscale) banner — a soft
              brand tint, slightly deeper at the bottom so the text stays legible. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{
              background: `linear-gradient(160deg, ${primary}4D 0%, ${primary}B3 100%)`,
            }}
          />
          {!embedded && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white text-lg leading-none transition z-10"
            >
              ×
            </button>
          )}
          <div className="relative z-10 w-24 h-24 mx-auto">
            <div className="w-24 h-24 rounded-3xl bg-surface shadow-lg ring-4 ring-white/30 overflow-hidden flex items-center justify-center text-3xl font-bold" style={{ color: primary }}>
              {img ? (
                <img src={img} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                initial
              )}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              aria-label="Change photo"
              className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-surface shadow-md border flex items-center justify-center hover:bg-gray-50 disabled:opacity-60"
              style={{ color: primary }}
            >
              {uploading ? (
                <Spinner size={16} />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 8h.01M4 16l4-4a3 3 0 014 0l4 4M14 14l1-1a3 3 0 014 0l1 1M4 6h4l1.5-2h5L16 6h4a1 1 0 011 1v11a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                </svg>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
            />
          </div>
          <h3 className="text-white text-2xl font-bold mt-5 relative z-10" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>{name || member?.name}</h3>
          <p className="text-white/95 text-sm mt-1 relative z-10" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>{data?.email || member?.email}</p>
          {data?.joined_on && (
            <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white relative z-10">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
              </svg>
              Member since {data.joined_on}
            </span>
          )}
          {/* Upload / change, and — only once there's a photo to drop — remove. */}
          <div className="mt-3 flex items-center justify-center gap-3 relative z-10">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || removing}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/95 underline underline-offset-2 hover:text-white disabled:opacity-60"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12V4m0 0L8 8m4-4l4 4" />
              </svg>
              {uploading ? "Uploading…" : img ? "Change photo" : "Upload photo"}
            </button>
            {img && (
              <>
                <span aria-hidden="true" className="h-3 w-px bg-white/40" />
                <button
                  type="button"
                  onClick={removeAvatar}
                  disabled={uploading || removing}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/95 underline underline-offset-2 hover:text-white disabled:opacity-60"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M10 7V5.5A1.5 1.5 0 0111.5 4h1A1.5 1.5 0 0114 5.5V7m-6 0v11a1 1 0 001 1h6a1 1 0 001-1V7" />
                  </svg>
                  {removing ? "Removing…" : "Remove photo"}
                </button>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <InlineLoader className="justify-center p-8" label="Loading profile…" primary={primary} />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto scrollbar-hide p-5 sm:p-7 bg-gray-50/50">
            {/* Edit form */}
            <form id="profileForm" onSubmit={save} className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-semibold tracking-[0.12em] text-gray-400 uppercase">Profile details</span>
                <span className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Name + Phone — side by side on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Name"
                  value={name}
                  onChange={(v) => {
                    setName(v.slice(0, NAME_LIMITS.max));
                    if (errors.name) setErrors((p) => ({ ...p, name: "" }));
                  }}
                  disabled={saving}
                  primary={primary}
                  icon="user"
                  error={errors.name}
                />
                <PhoneField value={phone} onChange={setPhone} disabled={saving} primary={primary} />
              </div>

              {/* About */}
              <label className="block">
                <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                  About
                  <span className="text-gray-400 font-normal">(optional)</span>
                </span>
                <textarea
                  value={about}
                  rows={3}
                  maxLength={1000}
                  disabled={saving}
                  placeholder="Tell the community a bit about yourself…"
                  onChange={(e) => setAbout(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = primary;
                    e.target.style.boxShadow = `0 0 0 3px ${primary}22`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgb(var(--c-gray-200))";
                    e.target.style.boxShadow = "none";
                  }}
                  aria-invalid={errors.about ? "true" : undefined}
                  className="w-full rounded-xl border px-3.5 py-2.5 text-sm text-gray-800 outline-none resize-none"
                  style={{ borderColor: errors.about ? "#dc2626" : "rgb(var(--c-gray-200))" }}
                />
              </label>

              {/* Location search + preview map */}
              <LocationPicker
                token={token}
                primary={primary}
                value={loc}
                onChange={(v) => {
                  setLoc(v);
                  if (errors.location) setErrors((p) => ({ ...p, location: "" }));
                }}
                error={errors.location}
              />
            </form>

            {/* Renders nothing unless this creator has opened products up to
                people who bought what this member bought — which, for most
                members of most memberships, is never. */}
            <AffiliateEarnSection
              token={token}
              memberToken={member?.member_token}
              primary={primary}
            />
          </div>
        )}

        {/* ── Pinned footer ── */}
        {!loading && (
          <div className="shrink-0 border-t border-gray-100 bg-surface px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
            {!embedded && (
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-xl transition disabled:opacity-60"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              form="profileForm"
              disabled={saving}
              style={{
                background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -18)} 100%)`,
                boxShadow: `0 8px 20px -8px ${primary}99`,
              }}
              className="flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-xl hover:opacity-95 active:scale-[0.99] transition disabled:opacity-60"
            >
              {saving && <Spinner size={16} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const FIELD_ICONS = {
  user: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  phone: "M3 5a2 2 0 012-2h2.6a1 1 0 01.97.757l1.1 4.4a1 1 0 01-.27.95l-1.7 1.7a13 13 0 006.2 6.2l1.7-1.7a1 1 0 01.95-.27l4.4 1.1a1 1 0 01.76.97V19a2 2 0 01-2 2h-1C9.7 21 3 14.3 3 6V5z",
};

function Field({ label, value, onChange, optional, disabled, primary, icon, error }) {
  const [focused, setFocused] = useState(false);
  const iconPath = icon && FIELD_ICONS[icon];
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
        {label}
        {optional && <span className="text-gray-400 font-normal">(optional)</span>}
      </span>
      <div className="relative">
        {iconPath && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors" style={focused ? { color: primary } : undefined}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
            </svg>
          </span>
        )}
        <input
          type="text"
          value={value}
          /* No `required`: the browser would block submit with its own native
             "Please fill in this field." bubble, so the styled message below
             never got a chance to render. save() validates instead. */
          aria-invalid={error ? "true" : undefined}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-xl border ${iconPath ? "pl-9" : "pl-3.5"} pr-3.5 py-2.5 text-sm text-gray-800 outline-none`}
          style={{
            borderColor: error ? "#dc2626" : focused ? primary : "rgb(var(--c-gray-200))",
            boxShadow: error
              ? "0 0 0 3px rgba(220,38,38,0.12)"
              : focused
              ? `0 0 0 3px ${primary}22`
              : "none",
          }}
        />
      </div>
      {error ? (
        <span className="mt-1.5 block text-[12px] font-medium text-red-600 dark:text-red-300">{error}</span>
      ) : null}
    </label>
  );
}

function shade(hex, percent) {
  try {
    let h = (hex || "").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return hex;
    const num = parseInt(h, 16);
    const amt = Math.round(2.55 * percent);
    const r = Math.max(0, Math.min(255, (num >> 16) + amt));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
    const b = Math.max(0, Math.min(255, (num & 0xff) + amt));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch {
    return hex;
  }
}

export default ProfileModal;
